import { useEffect, useRef } from "react";

const SCROLL_LOCKED_CLASS = "scroll-locked";

/**
 * Shared hook for drawer/modal behavior (ESC key, body scroll lock, focus management)
 * Used by NavDrawer and Filters drawer to ensure consistent behavior.
 * Locks scroll via wheel/touch listeners only so the scrollbar stays visible and styled as inactive.
 */
export function useDrawer(isOpen: boolean, onClose: () => void) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // ESC key handler
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  // Lock scroll when drawer is open: only add class + block wheel/touch (scrollbar stays visible).
  // Не блокуємо скрол всередині самої панелі drawer — інакше не працює скрол у списках фільтрів.
  useEffect(() => {
    if (!isOpen) return;

    document.documentElement.classList.add(SCROLL_LOCKED_CLASS);

    const preventScroll = (e: Event) => {
      const target = e.target as Node | null;
      if (panelRef.current?.contains(target)) return;
      e.preventDefault();
    };

    const scrollKeys = new Set([
      " ",
      "PageUp",
      "PageDown",
      "Home",
      "End",
      "ArrowUp",
      "ArrowDown",
    ]);
    const onKeyDownScroll = (e: KeyboardEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      if (scrollKeys.has(e.key)) e.preventDefault();
    };

    document.addEventListener("wheel", preventScroll, { passive: false });
    document.addEventListener("touchmove", preventScroll, { passive: false });
    document.addEventListener("keydown", onKeyDownScroll, { capture: true });

    return () => {
      document.documentElement.classList.remove(SCROLL_LOCKED_CLASS);
      document.removeEventListener("wheel", preventScroll);
      document.removeEventListener("touchmove", preventScroll);
      document.removeEventListener("keydown", onKeyDownScroll, { capture: true });
    };
  }, [isOpen]);

  // Focus management
  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => panelRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  return panelRef;
}
